import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { PaymentStatusBadge } from "../PaymentStatusBadge";
import { PaymentActions } from "../PaymentActions";
import type { Payment } from "./types";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { CalendarClock, CreditCard, Receipt, User } from "lucide-react";

interface PaymentDetailsDialogProps {
  payment: Payment | null;
  isOpen: boolean;
  onClose: () => void;
}

export function PaymentDetailsDialog({
  payment,
  isOpen,
  onClose,
}: PaymentDetailsDialogProps) {
  if (!payment) return null;

  const formatDescription = (payment: Payment) => {
    if (!payment.tax_return_requests) return "Payment";
    const { service_type, financial_year } = payment.tax_return_requests;
    return `${service_type} - FY${financial_year}`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-primary" />
            <span>Payment Details</span>
          </DialogTitle>
          <DialogDescription>
            View details for transaction {payment.id.substring(0, 8)}...
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-6">
          {/* Status Banner */}
          <div
            className={cn(
              "p-3 rounded-lg border flex items-center justify-between",
              payment.status.toLowerCase() === "completed"
                ? "bg-green-50 border-green-100 dark:bg-green-950/30 dark:border-green-900/50"
                : payment.status.toLowerCase() === "pending"
                ? "bg-yellow-50 border-yellow-100 dark:bg-yellow-950/30 dark:border-yellow-900/50"
                : payment.status.toLowerCase() === "processing"
                ? "bg-blue-50 border-blue-100 dark:bg-blue-950/30 dark:border-blue-900/50"
                : "bg-red-50 border-red-100 dark:bg-red-950/30 dark:border-red-900/50"
            )}
          >
            <div className="flex items-center gap-2">
              <PaymentStatusBadge status={payment.status} />
              <span className="text-sm font-medium">
                {payment.status.toLowerCase() === "completed"
                  ? "Payment completed successfully"
                  : payment.status.toLowerCase() === "pending"
                  ? "Payment is pending processing"
                  : payment.status.toLowerCase() === "processing"
                  ? "Payment is being processed"
                  : "Payment requires attention"}
              </span>
            </div>
            <div className="text-lg font-semibold">
              ${payment.amount.toFixed(2)} AUD
            </div>
          </div>

          {/* Payment Details */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-2">
                <CalendarClock className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Date
                  </p>
                  <p className="mt-1 font-medium">
                    {format(new Date(payment.created_at), "PPP")}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <CreditCard className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Payment Method
                  </p>
                  <p className="mt-1 font-medium">Credit Card</p>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <Receipt className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Service
                  </p>
                  <p className="mt-1 font-medium">
                    {formatDescription(payment)}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <User className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Client
                  </p>
                  <p className="mt-1 font-medium">
                    {payment.profiles?.full_name || "Client"}
                  </p>
                </div>
              </div>
            </div>

            <Separator />

            <div>
              <p className="text-sm font-medium text-muted-foreground mb-1">
                Transaction ID
              </p>
              <p className="font-mono text-xs bg-muted p-2 rounded-md overflow-x-auto">
                {payment.id}
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="flex justify-between items-center sm:justify-between">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <PaymentActions paymentId={payment.id} />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
